<template>
    <div class="min-h-screen bg-[#F6F8FA] font-sans text-slate-900">
        <header class="border-b border-slate-200 bg-white">
            <div class="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 sm:px-6">
                <a href="/backstage/audio-ads" class="flex items-center gap-2.5 text-sm text-slate-500">
                    <img src="@/assets/logo-full.svg" alt="Heesify" class="h-8 w-auto brightness-0" />
                    <span class="border-l border-slate-200 pl-2.5">Audio ads</span>
                </a>
                <a href="/backstage/audio-ads" class="text-sm font-medium text-slate-600 hover:text-slate-900">Back to ads</a>
            </div>
        </header>

        <main class="mx-auto max-w-5xl px-4 py-8 sm:px-6">
            <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 class="text-2xl font-semibold tracking-tight">Plans</h1>
                    <p class="mt-1 text-sm text-slate-500">What sponsors can buy. Changes only apply to new ads. Ads already bought keep what they paid for.</p>
                </div>
                <button type="button" class="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60" :disabled="!ready" @click="openNew">New plan</button>
            </div>

            <p v-if="!ready && loaded" class="mt-6 rounded-lg bg-amber-100 px-4 py-3 text-sm text-amber-800">
                These are the default plans. Run <code class="font-semibold">php artisan migrate</code> on the server to manage them here.
            </p>
            <p v-if="error" role="alert" class="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{{ error }}</p>

            <div class="mt-6 grid gap-4 md:grid-cols-2">
                <article v-for="plan in plans" :key="plan.key" class="flex flex-col rounded-xl border border-slate-200 bg-white p-5" :class="plan.active ? '' : 'opacity-70'">
                    <div class="flex items-start justify-between gap-3">
                        <div class="min-w-0">
                            <h2 class="truncate font-semibold">{{ plan.label }}</h2>
                            <p class="mt-0.5 text-sm text-slate-500">{{ plan.hint }}</p>
                        </div>
                        <p class="shrink-0 text-xl font-semibold">{{ money(plan.price) }}</p>
                    </div>

                    <dl class="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4 text-sm">
                        <div>
                            <dt class="text-xs text-slate-500">{{ plan.type === "plays" ? "Plays" : "Play limit (hidden from sponsors)" }}</dt>
                            <dd class="mt-0.5 font-medium">{{ plan.impressions.toLocaleString() }}</dd>
                        </div>
                        <div>
                            <dt class="text-xs text-slate-500">Ads on this plan</dt>
                            <dd class="mt-0.5 font-medium">{{ plan.ads.toLocaleString() }}</dd>
                        </div>
                    </dl>

                    <div class="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
                        <label class="flex items-center gap-2 text-sm font-medium text-slate-700">
                            <button
                                type="button"
                                role="switch"
                                :aria-checked="plan.active"
                                :aria-label="`${plan.label} on sale`"
                                class="relative h-5 w-9 rounded-full transition"
                                :class="plan.active ? 'bg-slate-900' : 'bg-slate-300'"
                                :disabled="busy || !ready"
                                @click="toggle(plan)"
                            >
                                <span class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition" :class="plan.active ? 'translate-x-4' : ''"></span>
                            </button>
                            {{ plan.active ? "On sale" : "Off sale" }}
                        </label>

                        <div v-if="confirming === plan.key" class="flex items-center gap-2 text-sm">
                            <span class="text-slate-600">Remove this plan?</span>
                            <button type="button" class="rounded-md bg-red-600 px-3 py-1.5 font-medium text-white" :disabled="busy" @click="remove(plan)">Remove</button>
                            <button type="button" class="rounded-md border border-slate-300 px-3 py-1.5 font-medium text-slate-700" @click="confirming = null">Cancel</button>
                        </div>
                        <div v-else class="flex items-center gap-2">
                            <button type="button" class="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60" :disabled="!ready" @click="openEdit(plan)">Edit</button>
                            <button
                                type="button"
                                class="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                :disabled="!ready || plan.ads > 0"
                                :title="plan.ads > 0 ? 'Ads use this plan, so it can\'t be removed. Take it off sale instead.' : ''"
                                @click="confirming = plan.key"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                    <p v-if="plan.ads > 0" class="mt-2 text-xs text-slate-500">Ads use this plan, so it can't be removed. Take it off sale instead.</p>
                </article>
            </div>
        </main>

        <div v-if="form" class="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 sm:items-center sm:p-4" @click.self="form = null">
            <form role="dialog" aria-modal="true" :aria-label="editing ? 'Edit plan' : 'New plan'" novalidate class="max-h-full w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-6 sm:rounded-2xl" @submit.prevent="save">
                <h2 class="text-lg font-semibold">{{ editing ? "Edit plan" : "New plan" }}</h2>

                <div class="mt-5 space-y-4">
                    <div>
                        <p class="text-sm font-medium text-slate-700">Sell by</p>
                        <div class="mt-1.5 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 text-sm font-medium">
                            <button type="button" class="rounded-md py-1.5 disabled:cursor-not-allowed" :class="form.type === 'plays' ? 'bg-white shadow-sm' : 'text-slate-600'" :disabled="typeLocked" @click="form.type = 'plays'">Number of plays</button>
                            <button type="button" class="rounded-md py-1.5 disabled:cursor-not-allowed" :class="form.type === 'time' ? 'bg-white shadow-sm' : 'text-slate-600'" :disabled="typeLocked" @click="form.type = 'time'">Length of time</button>
                        </div>
                        <p v-if="typeLocked" class="mt-1.5 text-xs text-slate-500">Ads use this plan, so its type is locked.</p>
                        <p v-else-if="errors.type" class="mt-1.5 text-sm text-red-600">{{ errors.type }}</p>
                    </div>

                    <label class="block">
                        <span class="text-sm font-medium text-slate-700">Name</span>
                        <input v-model="form.label" maxlength="40" :placeholder="form.type === 'plays' ? '2,000 plays' : 'One month'" :class="input(errors.label)" />
                        <span v-if="errors.label" class="mt-1.5 block text-sm text-red-600">{{ errors.label }}</span>
                        <span v-else class="mt-1.5 block text-xs text-slate-500">Sponsors see this.</span>
                    </label>

                    <div class="grid grid-cols-2 gap-3">
                        <label class="block">
                            <span class="text-sm font-medium text-slate-700">Price (USD)</span>
                            <input v-model.number="form.price" type="number" min="1" step="0.01" inputmode="decimal" :class="input(errors.price)" />
                            <span v-if="errors.price" class="mt-1.5 block text-sm text-red-600">{{ errors.price }}</span>
                        </label>
                        <label v-if="form.type === 'time'" class="block">
                            <span class="text-sm font-medium text-slate-700">Runs for (days)</span>
                            <input v-model.number="form.days" type="number" min="1" max="365" inputmode="numeric" :class="input(errors.days)" />
                            <span v-if="errors.days" class="mt-1.5 block text-sm text-red-600">{{ errors.days }}</span>
                        </label>
                    </div>

                    <label class="block">
                        <span class="text-sm font-medium text-slate-700">{{ form.type === "plays" ? "Plays" : "Most plays in that time" }}</span>
                        <input v-model.number="form.impressions" type="number" min="1" inputmode="numeric" :class="input(errors.impressions)" />
                        <span v-if="errors.impressions" class="mt-1.5 block text-sm text-red-600">{{ errors.impressions }}</span>
                        <span v-else class="mt-1.5 block text-xs text-slate-500">{{ form.type === "plays" ? "Sponsors see this as their goal." : "Never shown to sponsors. Plays spread evenly across the time." }}</span>
                    </label>

                    <div class="rounded-lg bg-slate-50 p-3">
                        <p class="text-xs text-slate-500">What sponsors see</p>
                        <div class="mt-1 flex items-center justify-between gap-3">
                            <span class="min-w-0 truncate text-sm font-medium">{{ form.label || "Plan name" }}</span>
                            <span class="font-semibold">{{ money(form.price || 0) }}</span>
                        </div>
                        <p class="mt-0.5 text-xs text-slate-500">{{ form.type === "plays" ? "Runs until every play is used" : `Runs for ${form.days || "?"} days` }}</p>
                    </div>
                </div>

                <p v-if="errors.active" class="mt-3 text-sm text-red-600">{{ errors.active }}</p>
                <div class="mt-6 flex justify-end gap-2">
                    <button type="button" class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700" @click="form = null">Cancel</button>
                    <button type="submit" class="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" :disabled="busy">{{ busy ? "Saving..." : editing ? "Save changes" : "Add plan" }}</button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import axios from "axios";

const API = "/api/backstage/audio-ads/plans";

export default {
    // A clean page of its own, like /sponsor, not the dark backstage layout
    layout: (_, page) => page,
    data() {
        return { plans: [], ready: true, loaded: false, form: null, editing: null, confirming: null, busy: false, error: null, errors: {} };
    },
    computed: {
        typeLocked() {
            return Boolean(this.editing?.ads);
        },
    },
    mounted() {
        this.load();
    },
    methods: {
        async load() {
            try {
                this.apply((await axios.get(API)).data);
            } catch (error) {
                this.error = "Couldn't load the plans. Please refresh.";
            }
        },
        apply(data) {
            this.plans = data.plans;
            this.ready = data.ready;
            this.loaded = true;
        },
        money: (value) => `$${Number(value).toFixed(2)}`,
        input(error) {
            return [
                "mt-1.5 block w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:ring-1 focus:ring-slate-900",
                error ? "border-red-400" : "border-slate-300",
            ];
        },
        openNew() {
            this.editing = null;
            this.errors = {};
            this.form = { label: "", price: "", type: "plays", impressions: "", days: 30, active: true };
        },
        openEdit(plan) {
            this.editing = plan;
            this.errors = {};
            this.form = { label: plan.label, price: plan.price, type: plan.type, impressions: plan.impressions, days: plan.days ?? 30, active: plan.active };
        },
        firstErrors(error) {
            const found = error.response?.data?.errors ?? {};
            return Object.fromEntries(Object.entries(found).map(([key, list]) => [key, list[0]]));
        },
        async save() {
            this.busy = true;
            this.errors = {};
            this.error = null;
            try {
                const url = this.editing ? `${API}/${this.editing.key}` : API;
                this.apply((await axios.post(url, this.form)).data);
                this.form = null;
            } catch (error) {
                this.errors = this.firstErrors(error);
                if (!Object.keys(this.errors).length) this.errors = { label: "That didn't go through. Please try again." };
            }
            this.busy = false;
        },
        async toggle(plan) {
            this.busy = true;
            this.error = null;
            try {
                const { label, price, type, impressions, days } = plan;
                this.apply((await axios.post(`${API}/${plan.key}`, { label, price, type, impressions, days, active: !plan.active })).data);
            } catch (error) {
                this.error = Object.values(this.firstErrors(error))[0] ?? "That didn't go through. Please try again.";
            }
            this.busy = false;
        },
        async remove(plan) {
            this.busy = true;
            this.error = null;
            try {
                this.apply((await axios.delete(`${API}/${plan.key}`)).data);
            } catch (error) {
                this.error = Object.values(this.firstErrors(error))[0] ?? "That didn't go through. Please try again.";
            }
            this.confirming = null;
            this.busy = false;
        },
    },
};
</script>
